import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

/**
 * Strip leading bullets/asterisks (ASCII + Unicode) and trailing footnote refs like (*)
 */
export function cleanTermText(text) {
    const LEAD_BULLET = /^\s*[\*\u2217\u2731\u2732\u2733\u2734\u2735\u2736\u2737\u2738\u2739\u274B\u2749\u274A\u272D\u272E\u272B\u272C\u272A\u22C6\u2605\u2606\u2022\u25AA\u25CF\u25A0\u25CB\u25E6]+\s*/
    const TRAIL_FOOTNOTE = /\s*\(\*\)\s*$/
    return (text || '').replace(LEAD_BULLET, '').replace(TRAIL_FOOTNOTE, '').trim()
}

/**
 * useArchTerms — fetch and manage arch_terms_conditions rows from Supabase.
 */
export function useArchTerms(session) {
    const [items,   setItems]   = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)
    const userId = session?.user?.id || null

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('arch_terms_conditions', userId).catch(() => null)
        if (cached) {
            setItems(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_terms_conditions')
            .select('*')
            .order('source_file', { ascending: true })
            .order('sort_order',  { ascending: true })
        if (err) {
            console.error('[archTerms] fetch error:', err.message)
            setError(err.message)
            if (!cached) setItems([])
        } else {
            setItems(data || [])
            await saveReferenceCache('arch_terms_conditions', data || [], userId).catch(() => {})
        }
        setLoading(false)
    }, [userId])

    useEffect(() => { load() }, [load])

    const createTerm = useCallback(async (fields) => {
        const payload = {
            market:         fields.market,
            source_file:    fields.source_file || 'custom',
            project_types:  fields.project_types || null,
            phase:          fields.phase || null,
            service_groups: fields.service_groups || null,
            addon_key:      fields.addon_key || null,
            text:           fields.text?.trim(),
            section_title:  fields.section_title || null,
            is_section_title: fields.is_section_title ?? false,
            sort_order:     fields.sort_order ?? 0,
            is_active:      fields.is_active ?? true,
            created_by:     userId,
            updated_by:     userId,
        }
        if (!payload.text) throw new Error('text is required')
        const { data, error: err } = await supabase
            .from('arch_terms_conditions')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setItems(prev => [...prev, data].sort(termSort))
        await clearReferenceCache('arch_terms_conditions', userId).catch(() => {})
        return data
    }, [userId])

    const updateTerm = useCallback(async (id, patch) => {
        if (patch.text) patch.text = patch.text.trim()
        const payload = { ...patch, updated_by: userId, updated_at: new Date().toISOString() }
        const { data, error: err } = await supabase
            .from('arch_terms_conditions')
            .update(payload)
            .eq('id', id)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setItems(prev => prev.map(t => t.id === id ? data : t).sort(termSort))
        await clearReferenceCache('arch_terms_conditions', userId).catch(() => {})
        return data
    }, [userId])

    const deleteTerm = useCallback(async (id) => {
        let savedItem = null
        setItems(prev => {
            savedItem = prev.find(t => t.id === id) ?? null
            return prev.filter(t => t.id !== id)
        })
        const { error: err } = await supabase
            .from('arch_terms_conditions')
            .delete()
            .eq('id', id)
        if (err) {
            if (savedItem) setItems(prev => [...prev, savedItem].sort(termSort))
            console.error('[archTerms] delete error:', err.message)
            throw new Error(err.message)
        }
        await clearReferenceCache('arch_terms_conditions', userId).catch(() => {})
    }, [userId])

    const renameSourceFile = useCallback(async (oldName, newName) => {
        if (!oldName || !newName || oldName === newName) return
        const { error: err } = await supabase
            .from('arch_terms_conditions')
            .update({ source_file: newName, updated_by: userId, updated_at: new Date().toISOString() })
            .eq('source_file', oldName)
        if (err) throw new Error(err.message)
        setItems(prev => prev.map(t => t.source_file === oldName ? { ...t, source_file: newName } : t).sort(termSort))
        await clearReferenceCache('arch_terms_conditions', userId).catch(() => {})
    }, [userId])

    const deleteSourceFile = useCallback(async (sourceFile) => {
        const { error: err } = await supabase
            .from('arch_terms_conditions')
            .delete()
            .eq('source_file', sourceFile)
        if (err) throw new Error(err.message)
        setItems(prev => prev.filter(t => t.source_file !== sourceFile))
        await clearReferenceCache('arch_terms_conditions', userId).catch(() => {})
    }, [userId])

    const reparseFromFiles = useCallback(async () => {
        // Delete all non-custom terms and re-insert from .docx via edge function
        // Frontend calls the terms-migrate edge function (which expects admin auth)
        // For now, this is a placeholder that the admin runs via CLI script
        throw new Error('Re-parse must be run via: node scripts/extract-terms-v2.py')
    }, [])

    const reorderTerms = useCallback(async (orderedIds, baseSort = 0) => {
        if (!orderedIds || orderedIds.length === 0) return
        setItems(prev => {
            const updatedMap = new Map()
            orderedIds.forEach((id, idx) => updatedMap.set(id, baseSort + idx))
            const next = prev.map(item => {
                if (updatedMap.has(item.id)) {
                    return { ...item, sort_order: updatedMap.get(item.id) }
                }
                return item
            })
            return next.sort(termSort)
        })
        try {
            for (let idx = 0; idx < orderedIds.length; idx++) {
                const id = orderedIds[idx]
                await supabase
                    .from('arch_terms_conditions')
                    .update({ sort_order: baseSort + idx, updated_by: userId, updated_at: new Date().toISOString() })
                    .eq('id', id)
            }
            await clearReferenceCache('arch_terms_conditions', userId).catch(() => {})
        } catch (e) {
            console.error('[archTerms] reorder error:', e)
            throw e
        }
    }, [userId])

    return { items, loading, error, createTerm, updateTerm, deleteTerm, renameSourceFile, deleteSourceFile, reparseFromFiles, reload: load, reorderTerms }
}

function termSort(a, b) {
    if (a.source_file !== b.source_file) return a.source_file.localeCompare(b.source_file)
    return (a.sort_order ?? 0) - (b.sort_order ?? 0)
}

/**
 * useArchTermsSourceDefaults — manage which T&C source-file groups are
 * auto-included when a new estimate is created.
 */
export function useArchTermsSourceDefaults() {
    const [rows,    setRows]    = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const { data, error: err } = await supabase
            .from('arch_terms_source_defaults')
            .select('*')
            .order('source_file', { ascending: true })
        if (err) {
            console.error('[archTermsSourceDefaults] fetch error:', err.message)
            setError(err.message)
            setRows([])
        } else {
            setRows(data || [])
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    const defaultSourceFiles = useMemo(() => {
        const s = new Set()
        for (const r of rows) { if (r.is_default) s.add(r.source_file) }
        return s
    }, [rows])

    const setSourceDefault = useCallback(async (sourceFile, isDefault) => {
        // Optimistic
        setRows(prev => {
            const exists = prev.find(r => r.source_file === sourceFile)
            if (exists) return prev.map(r => r.source_file === sourceFile ? { ...r, is_default: isDefault } : r)
            return [...prev, { source_file: sourceFile, is_default: isDefault, updated_at: new Date().toISOString() }]
        })
        const { data, error: err } = await supabase
            .from('arch_terms_source_defaults')
            .upsert({ source_file: sourceFile, is_default: isDefault, updated_at: new Date().toISOString() }, { onConflict: 'source_file' })
            .select()
            .single()
        if (err) {
            console.error('[archTermsSourceDefaults] upsert error:', err.message)
            await load()
            throw new Error(err.message)
        }
        setRows(prev => {
            const exists = prev.find(r => r.source_file === sourceFile)
            if (exists) return prev.map(r => r.source_file === sourceFile ? data : r)
            return [...prev, data].sort((a, b) => a.source_file.localeCompare(b.source_file))
        })
    }, [load])

    return { rows, loading, error, defaultSourceFiles, setSourceDefault, reload: load }
}

/**
 * Build the initial estimate.terms array from catalog items for default source files.
 * Only includes is_active items from defaultSourceFiles whose market matches the estimate's market.
 * @param {Array} catalogTerms - all items from useArchTerms
 * @param {Set<string>} defaultSourceFiles - source_files marked as default
 * @param {string} estimateMarket - e.g. 'FL_RES', 'TX_COM'
 * @returns {Array} terms array ready for estimate.terms
 */
export function getDefaultTermsFromCatalog(catalogTerms, defaultSourceFiles, estimateMarket) {
    if (!catalogTerms || !defaultSourceFiles || defaultSourceFiles.size === 0) return []
    const statePrefix = estimateMarket?.startsWith('TX') ? 'TX' : 'FL'
    return (catalogTerms || [])
        .filter(item => {
            if (!defaultSourceFiles.has(item.source_file)) return false
            if (!item.is_active) return false
            if (item.market && item.market !== 'both' && item.market !== statePrefix) return false
            return true
        })
        .sort((a, b) => {
            if (a.source_file !== b.source_file) return a.source_file.localeCompare(b.source_file)
            return (a.sort_order ?? 0) - (b.sort_order ?? 0)
        })
        .map(item => ({
            id:               item.id,
            text:             cleanTermText(item.text || ''),
            included:         true,
            sort_order:       item.sort_order ?? 0,
            source_file:      item.source_file,
            section_title:    item.section_title ?? null,
            is_section_title: item.is_section_title ?? false,
            is_bullet:        item.is_bullet ?? false,
        }))
}

/**
 * Normalize DB values: empty arrays, empty strings, and null-ish arrays → null.
 */
function normalizeDbValue(v) {
    if (v == null) return null
    if (Array.isArray(v)) {
        const cleaned = v.filter(x => x != null && x !== '')
        return cleaned.length > 0 ? cleaned : null
    }
    if (typeof v === 'string' && v.trim() === '') return null
    return v
}

/**
 * Infer project_types from term metadata when the DB field is null.
 * Checks source_file, section_title, and text for indicators.
 */
function inferProjectTypes(item) {
    const haystacks = [
        item.source_file,
        item.section_title,
        item.text,
    ].filter(Boolean).map(s => s.toLowerCase())

    const types = new Set()
    for (const hay of haystacks) {
        // Residential
        if (hay.includes('res_new') || hay.includes('new construction') || hay.includes('new residential')) types.add('res_new')
        if (hay.includes('res_add') || hay.includes('add_alt') || hay.includes('addition') || hay.includes('alteration') || hay.includes('add/alt')) types.add('res_add_alt')
        if (hay.includes('res_rem') || hay.includes('remodel') || hay.includes('remodeling')) types.add('res_rem')
        // Commercial
        if (hay.includes('com_new') || hay.includes('commercial') && hay.includes('new')) types.add('com_new')
        if (hay.includes('com_add') || hay.includes('commercial') && (hay.includes('add') || hay.includes('alt'))) types.add('com_add_alt')
        if (hay.includes('com_rem') || hay.includes('commercial') && hay.includes('rem')) types.add('com_rem')
        // Civil / Addon
        if (hay.includes('civil') || hay.includes('site development') || hay.includes('site dev')) types.add('civil')
        if (hay.includes('addon') || hay.includes('site visit') || hay.includes('inspection') || hay.includes('energy report')) types.add('addon')
    }
    return types.size > 0 ? [...types] : null
}

/**
 * Infer phase from term metadata when the DB field is null.
 */
function inferPhase(item) {
    const haystacks = [
        item.source_file,
        item.section_title,
        item.text,
    ].filter(Boolean).map(s => s.toLowerCase())

    for (const hay of haystacks) {
        if (hay.includes('(cds)') || hay.includes('construction document') || hay.includes('construction documents')) return 'cds'
        if (hay.includes('(design)') || hay.includes('preliminary') || hay.includes('diseño') || hay.includes('design phase')) return 'design'
        if (hay.includes('both')) return 'both'
    }
    return null
}

/**
 * Suggest terms based on current project configuration.
 */
export function suggestTerms({
    market,
    projectType,
    phase,
    serviceGroups = [],
    addons,
    items,
}) {
    if (!items || items.length === 0) return []

    const statePrefix = market?.startsWith('FL') ? 'FL' : market?.startsWith('TX') ? 'TX' : null
    const enabledAddons = (addons || [])
        .filter(a => a.enabled === true)
        .map(a => a.key)

    const result = items.filter(item => {
        if (!item.is_active) return false
        if (statePrefix && item.market !== statePrefix && item.market !== 'both') return false

        // project_types: null = universal, otherwise must include projectType
        const itemProjectTypes = normalizeDbValue(item.project_types) ?? inferProjectTypes(item)
        if (itemProjectTypes && itemProjectTypes.length > 0) {
            if (!itemProjectTypes.includes(projectType)) return false
        }

        // phase: null = any, 'both' = both, otherwise must match
        const itemPhase = normalizeDbValue(item.phase) ?? inferPhase(item)
        if (itemPhase) {
            if (itemPhase !== 'both' && itemPhase !== phase) return false
        }

        // service_groups: null = any, otherwise must overlap
        if (item.service_groups && item.service_groups.length > 0) {
            const hasOverlap = serviceGroups.some(g => item.service_groups.includes(g))
            if (!hasOverlap) return false
        }

        // addon_key: null = core term, otherwise must match enabled addon
        if (item.addon_key) {
            if (!enabledAddons.includes(item.addon_key)) return false
        }

        return true
    })

    return result.sort((a, b) => {
        // Universal terms first, then by sort_order, then by source_file
        const aProjectTypes = normalizeDbValue(a.project_types)
        const aPhase = normalizeDbValue(a.phase)
        const aUniversal = !aProjectTypes && !aPhase && !a.service_groups && !a.addon_key ? 0 : 1
        const bProjectTypes = normalizeDbValue(b.project_types)
        const bPhase = normalizeDbValue(b.phase)
        const bUniversal = !bProjectTypes && !bPhase && !b.service_groups && !b.addon_key ? 0 : 1
        if (aUniversal !== bUniversal) return aUniversal - bUniversal
        if ((a.sort_order ?? 0) !== (b.sort_order ?? 0)) return (a.sort_order ?? 0) - (b.sort_order ?? 0)
        return a.source_file.localeCompare(b.source_file)
    })
}

/**
 * Check whether a custom term's own metadata matches the current project config.
 * A custom term with no filter metadata is always preserved (universal).
 */
function customTermMatchesConfig(term, config) {
    const termProjectTypes = normalizeDbValue(term.project_types)
    const termPhase = normalizeDbValue(term.phase)
    const termServiceGroups = normalizeDbValue(term.service_groups)
    // No filters = universal custom term, always keep
    if (!termProjectTypes && !termPhase && !termServiceGroups && !term.addon_key && !term.market) {
        return true
    }
    const statePrefix = config.market?.startsWith('FL') ? 'FL' : config.market?.startsWith('TX') ? 'TX' : null
    if (term.market && statePrefix && term.market !== statePrefix && term.market !== 'both') return false
    if (termProjectTypes && termProjectTypes.length > 0) {
        if (!config.projectType || !termProjectTypes.includes(config.projectType)) return false
    }
    if (termPhase) {
        if (termPhase !== 'both' && termPhase !== config.phase) return false
    }
    if (term.service_groups && term.service_groups.length > 0) {
        if (!config.serviceGroups || !config.serviceGroups.some(g => term.service_groups.includes(g))) return false
    }
    if (term.addon_key) {
        if (!config.enabledAddons || !config.enabledAddons.includes(term.addon_key)) return false
    }
    return true
}

/**
 * Merge suggested terms into an existing estimate.terms array.
 * Preserves user's included/excluded state and any text edits for terms that still match.
 * Removes .docx-sourced terms that no longer match.
 * Keeps custom terms only if their own metadata matches the current project config.
 */
export function mergeSuggestedTerms(existingTerms, suggestedItems, config = {}) {
    const existingMap = new Map((existingTerms || []).map(t => [t.id, t]))
    const suggestedMap = new Map((suggestedItems || []).map(t => [t.id, t]))

    const merged = []

    // For each suggested item, preserve existing state if present
    for (const item of (suggestedItems || [])) {
        const existing = existingMap.get(item.id)
        if (existing) {
            merged.push({
                ...existing,
                // Update metadata from catalog if it changed
                section_title: item.section_title ?? existing.section_title ?? null,
                sort_order: item.sort_order ?? existing.sort_order ?? 0,
                source_file: item.source_file || existing.source_file || 'custom',
                // If the catalog text changed, keep user's edited text but update metadata
                text: cleanTermText(existing.text || item.text || ''),
            })
        } else {
            merged.push({
                id: item.id,
                text: cleanTermText(item.text || ''),
                included: true,
                sort_order: item.sort_order ?? 0,
                source_file: item.source_file,
                section_title: item.section_title ?? null,
                is_section_title: item.is_section_title ?? false,
                is_bullet: item.is_bullet ?? false,
            })
        }
    }

    // Keep custom terms that are not in the suggestion list AND still match the current config
    for (const existing of (existingTerms || [])) {
        if (existing.source_file === 'custom' && !suggestedMap.has(existing.id)) {
            if (customTermMatchesConfig(existing, config)) {
                merged.push(existing)
            }
        }
    }

    // Sort: universal first, then sort_order, then source_file (same as suggestTerms)
    return merged.sort((a, b) => {
        const aProjectTypes = normalizeDbValue(a.project_types)
        const aPhase = normalizeDbValue(a.phase)
        const aUniversal = !aProjectTypes && !aPhase && !a.service_groups && !a.addon_key ? 0 : 1
        const bProjectTypes = normalizeDbValue(b.project_types)
        const bPhase = normalizeDbValue(b.phase)
        const bUniversal = !bProjectTypes && !bPhase && !b.service_groups && !b.addon_key ? 0 : 1
        if (aUniversal !== bUniversal) return aUniversal - bUniversal
        if ((a.sort_order ?? 0) !== (b.sort_order ?? 0)) return (a.sort_order ?? 0) - (b.sort_order ?? 0)
        return (a.source_file || '').localeCompare(b.source_file || '')
    })
}
